import { type ReactNode } from 'react';
import { useIntl } from 'react-intl';

import type { ChartDatapoint } from '../../../../core/types/chart-data.js';
import type { ChartUnit } from '../../../../core/types/chart-unit.js';
import type { Formatter } from '../../../../core/types/formatter.js';
import type { SeriesActionsTemplate } from '../../../../core/types/series-actions-template.js';
import type { XYChartTooltipPayload } from '../../../../xy-chart/types/xy-chart.js';
import { FlameTooltip } from '../../../shared/components/tooltip/FlameTooltip.js';
import { VALUE_FIELD } from '../../../shared/constants.js';
import { buildFlameTooltipBaseData } from '../../../shared/utils/build-tooltip-base-data.js';
import { formatFlameValue } from '../../../shared/utils/format-flame-value.js';
import { FLAME_CHART_TOOLTIP_MESSAGES } from './messages.js';

interface DefaultTooltipProps<TData extends ChartDatapoint> {
  payload: XYChartTooltipPayload;
  data: readonly TData[];
  labelAccessor: string;
  selfValueAccessor?: string;
  formatter?: Formatter;
  unit?: ChartUnit;
  seriesActions?: (datapoint: TData) => SeriesActionsTemplate;
  globalActions?: (datapoint: TData) => ReactNode[];
}

export function DefaultTooltip<TData extends ChartDatapoint>({
  payload,
  data,
  labelAccessor,
  selfValueAccessor,
  formatter,
  unit,
  seriesActions,
  globalActions,
}: Readonly<DefaultTooltipProps<TData>>): ReactNode {
  const intl = useIntl();
  const {
    datapoint,
    originalDatapoint,
    color,
    label,
    subheader,
    actions,
    selfValue,
  } = buildFlameTooltipBaseData({
    payload,
    data,
    labelAccessor,
    selfValueAccessor,
    formatter,
    unit,
    seriesActions,
    intl,
  });

  const resolvedGlobalActions =
    globalActions !== undefined && originalDatapoint !== undefined
      ? globalActions(originalDatapoint)
      : undefined;

  return (
    <FlameTooltip
      label={label}
      subheader={subheader}
      color={color}
      valueLabel={intl.formatMessage(FLAME_CHART_TOOLTIP_MESSAGES.duration)}
      totalValue={formatFlameValue(datapoint?.[VALUE_FIELD], formatter, unit)}
      selfValue={selfValue}
      actions={actions}
      globalActions={resolvedGlobalActions}
    />
  );
}
